"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";

import type { ComparisonRow } from "../../_hooks/useComparisonRows";
import { useHorizontalOverflow } from "../../_hooks/useHorizontalOverflow";
import { useRemovePlace } from "../../_hooks/useRemovePlace";
import { ADD_CITY_COLUMN_PX, TABLE_MIN_CITY_PX } from "../../_logic/constants";
import { DURATION, POP_IN, FADE } from "../../_logic/motion";
import type { CityEstimate } from "../../_logic/types";
import { AddCityHeader } from "./AddCityHeader";
import { RemovePlaceButton } from "./RemovePlaceButton";

const LABEL_CELL = "group/place sticky left-0 z-10 bg-white px-4 py-3.5 text-left align-top font-medium text-gray-900";
const CITY_CELL =
  "overflow-hidden whitespace-nowrap py-3.5 text-right align-top transition-colors duration-300 last:pr-4";
/** The "Add city" column: a quiet dashed edge, so it reads as room for one more city, not a city. */
const ADD_CELL = "border-l border-dashed border-l-gray-200 px-2";
const EMPHASIS = {
  normal: "",
  strong: "text-base font-bold text-gray-900",
  muted: "text-gray-600",
  route: "",
} as const;

interface ComparisonGridProps {
  cities: CityEstimate[];
  rows: ComparisonRow[];
  lowestCity: string | null;
  /**
   * `labelPx`: the pinned label column's width; the city columns share the rest. `dense`: city
   * columns are about 120–170px, so tighter padding, smaller totals, shorter captions. `bare`:
   * inside a summary row, so no card frame and no city header, which the row already shows.
   * `addCity`: end with an "Add city" column, for a comparison that has room for another city.
   */
  layout: { labelPx: number; dense: boolean; bare?: boolean; addCity?: boolean };
  /** Opens a city's detailed flight costs. */
  onOpenCity: (city: string) => void;
}

/**
 * The comparison as a table: one column per city, the label column pinned while it scrolls.
 * Used when every column fits; columns fade in and out as cities change.
 * @param props - Cities, the row registry and the cheapest city.
 * @returns The table card.
 * @throws Never.
 */
export const ComparisonGrid = ({ cities, rows, lowestCity, layout, onOpenCity }: ComparisonGridProps) => {
  const { labelPx, dense, bare = false, addCity = false } = layout;

  // Hooks
  const reduceMotion = useReducedMotion();
  const { removeDestination } = useRemovePlace();
  const { scrollerRef, overflowsRight } = useHorizontalOverflow<HTMLDivElement>([cities.length]);
  // A value that lands after the table opened fades in over its skeleton; one there from the start just shows.
  const opened = useRef(false);

  // Derived
  const cellMotion = reduceMotion ? FADE : POP_IN;
  const tint = (city: CityEstimate) => (city.city === lowestCity ? "bg-indigo-50" : "");
  const landing = opened.current && !reduceMotion ? { opacity: 0 } : false;
  const addColumn = addCity && !bare;

  // Effects
  useEffect(() => {
    opened.current = true;
  }, []);

  // Render
  return (
    <div className={`relative overflow-hidden bg-white ${bare ? "" : "rounded-2xl border border-gray-200"}`}>
      <div ref={scrollerRef} className="overflow-x-auto">
        {/* City columns shrink to TABLE_MIN_CITY_PX, then the table scrolls behind the pinned labels. */}
        <table
          className="w-full table-fixed border-separate border-spacing-0 text-sm font-medium tabular-nums text-gray-800"
          style={{ minWidth: labelPx + cities.length * TABLE_MIN_CITY_PX + (addColumn ? ADD_CITY_COLUMN_PX : 0) }}
        >
          <colgroup>
            <col style={{ width: labelPx }} />
            {cities.map((city) => (
              <col key={city.city} />
            ))}
            {addColumn && <col style={{ width: ADD_CITY_COLUMN_PX }} />}
          </colgroup>
          <thead data-tour={bare ? undefined : "estimator-comparison"} className={bare ? "hidden" : undefined}>
            <tr>
              <th scope="col" className={LABEL_CELL} style={{ width: labelPx }}>
                <span className="sr-only">Cost</span>
              </th>
              <AnimatePresence initial={false}>
                {cities.map((city) => (
                  <motion.th
                    key={city.city}
                    scope="col"
                    {...cellMotion}
                    className={`group/place relative ${CITY_CELL} whitespace-normal! ${dense ? "px-3" : "px-4"} font-semibold text-gray-900 ${tint(city)}`}
                  >
                    <span className="group/place flex max-w-full flex-col items-end gap-1.5">
                      <span className="flex max-w-full items-start justify-end gap-1">
                        {/* In a narrow column the remove button sits in the corner, so the name keeps the width. */}
                        <span className={dense ? "absolute left-1 top-1.5" : "pt-0.5 md:pt-1"}>
                          <RemovePlaceButton name={city.city} onRemove={() => removeDestination(city.city)} />
                        </span>
                        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
                        <button
                          type="button"
                          onClick={() => onOpenCity(city.city)}
                          title={`${city.city}: flight details`}
                          className={`min-w-0 rounded text-right text-base font-semibold leading-snug text-gray-900 ${dense ? "" : "md:text-lg md:leading-snug"} underline decoration-gray-300 underline-offset-4 transition-colors hover:decoration-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500`}
                        >
                          {/* ui-lint-disable-next-line truncate-no-tooltip: two balanced lines, then an ellipsis; the button's title has the full name, so a second title would only repeat it */}
                          <span className="line-clamp-2 wrap-break-word text-balance">{city.city}</span>
                        </button>
                      </span>
                      <AnimatePresence initial={false}>
                        {city.city === lowestCity && (
                          <motion.span
                            key="lowest"
                            {...FADE}
                            className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700"
                          >
                            Lowest
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                  </motion.th>
                ))}
              </AnimatePresence>
              {addColumn && (
                <th scope="col" className={`${ADD_CELL} py-3 align-top`}>
                  <AddCityHeader />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const divider = row.emphasis === "strong" ? "border-t border-gray-200" : "border-t border-gray-100";
              return (
                <tr key={row.key} className="group/row">
                  <th scope="row" className={`${LABEL_CELL} ${divider} ${EMPHASIS[row.emphasis]} text-sm!`}>
                    {row.labelNode ?? row.label}
                    {row.hint && <div className="text-xs font-medium text-gray-600">{row.hint}</div>}
                  </th>
                  <AnimatePresence initial={false}>
                    {cities.map((city) => (
                      <motion.td
                        key={city.city}
                        {...cellMotion}
                        className={`${CITY_CELL} ${dense ? "px-3" : "px-4"} ${divider} ${EMPHASIS[row.emphasis]} ${dense && row.emphasis === "strong" ? "text-sm!" : ""} ${tint(city)}`}
                      >
                        <motion.div
                          key={row.loading?.(city) ? "loading" : "value"}
                          initial={landing}
                          animate={{ opacity: 1 }}
                          transition={{ duration: DURATION.slow }}
                        >
                          {row.cell(city, cities.length === 1 ? "wide" : dense ? "narrow" : "column")}
                        </motion.div>
                      </motion.td>
                    ))}
                  </AnimatePresence>
                  {addColumn && <td aria-hidden className={`${ADD_CELL} ${divider}`} />}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {overflowsRight && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-linear-to-l from-white to-transparent"
        />
      )}
    </div>
  );
};
